function escapeAttribute(value: string) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');
}

const OPTIMIZABLE_IMAGE_HOSTS = new Set([
  'storage.googleapis.com',
  'deeply-website-backend.s3.ap-northeast-2.amazonaws.com',
  'cdn.aitimes.com',
  'cdn.ebn.co.kr',
  'cdn.epnc.co.kr',
  'cdn.it.chosun.com',
  'cdn.itdaily.kr',
  'cdn.kglobaltimes.com',
  'cdn.startupn.kr',
  'img1.daumcdn.net',
  'imgnews.pstatic.net',
  'it.donga.com',
  'www.boannews.com',
  'www.ddaily.co.kr',
  'www.hellot.net',
  'www.techm.kr',
]);
const RESPONSIVE_IMAGE_WIDTHS = [640, 828, 1200, 1920] as const;
const MINIMUM_OPTIMIZABLE_SOURCE_WIDTH = 500;

function hasAttribute(tag: string, attribute: string) {
  return new RegExp(`\\s${attribute}(?:\\s*=|\\s|/?>)`, 'i').test(tag);
}

function addAttribute(tag: string, attribute: string, value: string) {
  if (hasAttribute(tag, attribute)) {
    return tag;
  }

  return tag.replace(
    /^<([a-z][a-z0-9-]*)/i,
    `<$1 ${attribute}="${escapeAttribute(value)}"`,
  );
}

function setAttribute(tag: string, attribute: string, value: string) {
  const attributePattern = new RegExp(
    `\\s${attribute}\\s*=\\s*(?:"[^"]*"|'[^']*'|[^\\s>]+)`,
    'i',
  );
  const serializedAttribute = ` ${attribute}="${escapeAttribute(value)}"`;

  return attributePattern.test(tag)
    ? tag.replace(attributePattern, serializedAttribute)
    : addAttribute(tag, attribute, value);
}

function getAttribute(tag: string, attribute: string) {
  const match = tag.match(
    new RegExp(`\\s${attribute}\\s*=\\s*(["'])(.*?)\\1`, 'i'),
  );
  return match?.[2] ?? '';
}

function createYouTubeFacade(embedUrl: string, videoId: string, title: string) {
  const label = title || 'Play video';
  const thumbnailUrl = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;

  return `<!doctype html><html><head><meta name="viewport" content="width=device-width"></head><body style="margin:0;background:#000"><a href="${escapeAttribute(
    embedUrl,
  )}" aria-label="${escapeAttribute(
    label,
  )}" style="position:absolute;inset:0;display:grid;place-items:center;overflow:hidden"><img src="${thumbnailUrl}" alt="" style="width:100%;height:100%;object-fit:cover"><span aria-hidden="true" style="position:absolute;display:grid;place-items:center;width:64px;height:44px;border-radius:12px;background:rgba(0,0,0,.78);color:#fff;font:28px/1 sans-serif">▶</span></a></body></html>`;
}

function createOptimizedImageUrl(source: string, width: number) {
  return `/_next/image?url=${encodeURIComponent(source)}&w=${width}&q=82`;
}

function optimizeRemoteImageTag(tag: string) {
  const source = getAttribute(tag, 'src');
  const sourceWidth = Number(getAttribute(tag, 'width'));

  try {
    const url = new URL(source);
    const unsupportedFormat = /\.(?:gif|svg)(?:$|\?)/i.test(url.pathname);
    const sourceIsTooSmall =
      Number.isFinite(sourceWidth) &&
      sourceWidth > 0 &&
      sourceWidth < MINIMUM_OPTIMIZABLE_SOURCE_WIDTH;
    const outsideMediaBucket =
      url.hostname === 'storage.googleapis.com' &&
      !url.pathname.startsWith('/deeply-website-media/');

    if (
      !OPTIMIZABLE_IMAGE_HOSTS.has(url.hostname) ||
      outsideMediaBucket ||
      unsupportedFormat ||
      sourceIsTooSmall
    ) {
      return tag;
    }

    const srcSet = RESPONSIVE_IMAGE_WIDTHS.map(
      (width) => `${createOptimizedImageUrl(source, width)} ${width}w`,
    ).join(', ');
    let optimizedTag = setAttribute(
      tag,
      'src',
      createOptimizedImageUrl(source, 1200),
    );
    optimizedTag = setAttribute(optimizedTag, 'srcset', srcSet);
    return setAttribute(
      optimizedTag,
      'sizes',
      '(max-width: 768px) calc(100vw - 40px), 1000px',
    );
  } catch {
    return tag;
  }
}

function normalizeLowContrastInlineColors(content: string) {
  const lowContrastColors = [
    /color\s*:\s*#(?:868686|999999)\s*(?:!important)?/gi,
    /color\s*:\s*rgb\(\s*(?:128\s*,\s*127\s*,\s*127|134\s*,\s*134\s*,\s*134|153\s*,\s*153\s*,\s*153)\s*\)\s*(?:!important)?/gi,
    /color\s*:\s*hsl\(\s*0\s*,\s*0%\s*,\s*60%\s*\)\s*(?:!important)?/gi,
    /color\s*:\s*hsl\(\s*210\s*,\s*75%\s*,\s*60%\s*\)\s*(?:!important)?/gi,
  ];

  return lowContrastColors.reduce(
    (result, pattern) => result.replace(pattern, 'color:#54555b'),
    content,
  );
}

function enhanceTableHeaderRows(content: string) {
  return content.replace(
    /(<table\b[^>]*>[\s\S]*?<tr\b[^>]*>)([\s\S]*?)(<\/tr>)/gi,
    (row, openingTags: string, cells: string, closingTag: string) => {
      if (/<th\b/i.test(cells) || !/<td\b/i.test(cells)) {
        return row;
      }

      const headerCells = cells
        .replace(/<td(\b[^>]*)>/gi, '<th scope="col"$1>')
        .replace(/<\/td>/gi, '</th>');

      return `${openingTags}${headerCells}${closingTag}`;
    },
  );
}

function enhanceGenericLinkText(content: string) {
  return content.replace(
    /<a\b([^>]*\bhref\s*=\s*["']([^"']+)["'][^>]*)>(\s*)(here|link)(\s*)<\/a>/gi,
    (
      link,
      attributes: string,
      href: string,
      leading: string,
      label: string,
    ) => {
      try {
        const destination = new URL(href, 'https://deeplyinc.com');
        const context = `${destination.hostname} ${
          destination.pathname
            .split('/')
            .filter(Boolean)
            .at(-1)
            ?.replaceAll('-', ' ') ?? ''
        }`.trim();

        return `<a${attributes}>${leading}<span class="rich-text-visually-hidden">${escapeAttribute(
          context,
        )} </span>${label}</a>`;
      } catch {
        return link;
      }
    },
  );
}

export default function enhanceEmbeddedContentHtml(
  content: string,
  articleTitle = '',
) {
  const normalizedContent = normalizeLowContrastInlineColors(content);
  let imageIndex = 0;
  const withImages = normalizedContent.replace(
    /<img\b[^>]*>/gi,
    (originalTag) => {
      const isFirstImage = imageIndex === 0;
      imageIndex += 1;

      let tag = articleTitle
        ? addAttribute(originalTag, 'alt', articleTitle)
        : originalTag;
      tag = optimizeRemoteImageTag(tag);
      tag = setAttribute(tag, 'decoding', 'async');
      tag = setAttribute(tag, 'loading', isFirstImage ? 'eager' : 'lazy');

      return isFirstImage ? setAttribute(tag, 'fetchpriority', 'high') : tag;
    },
  );

  const withIframes = withImages.replace(/<iframe\b[^>]*>/gi, (originalTag) => {
    let tag = articleTitle
      ? addAttribute(originalTag, 'title', articleTitle)
      : originalTag;
    tag = tag.replace(
      /https:\/\/(?:www\.)?youtube\.com\/embed\//i,
      'https://www.youtube-nocookie.com/embed/',
    );

    const embedUrl = getAttribute(tag, 'src');
    const videoId = embedUrl.match(
      /youtube-nocookie\.com\/embed\/([^?&"']+)/i,
    )?.[1];
    if (videoId) {
      tag = setAttribute(tag, 'src', 'about:blank');
      tag = setAttribute(
        tag,
        'srcdoc',
        createYouTubeFacade(embedUrl, videoId, articleTitle),
      );
    }

    tag = setAttribute(tag, 'loading', 'lazy');
    return tag;
  });

  return enhanceGenericLinkText(enhanceTableHeaderRows(withIframes));
}
